import { useEffect, useMemo } from 'react';
import { DataPeriod, IntervalType } from '@Utils/enums';
import { useOneTimeReportStore } from '../../store';
import ReportDataPeriodSelector from './datePeriod';
import {
  Flex,
  TextLabel,
  Text,
  Stack,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { Tip } from './tip';
import style from '../../index.module.less';
import { useTranslation } from 'react-i18next';
import { dayjs, getTimePeriod } from '@Utils/time';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { Info } from '@Components/Common/Info';

const ReportDataPeriod = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();

  //getter
  const period = useOneTimeReportStore((store) => store.reportContent.period);

  //setter
  const setPeriod = useOneTimeReportStore((store) => store.setPeriod);

  //register
  const register = useOneTimeReportStore(
    (store) => store.registerResetCallback
  );
  const unregister = useOneTimeReportStore(
    (store) => store.unregisterResetCallback
  );

  const dataPeriodRange = useMemo(() => {
    const formatStr = t('common.momentShortDateFormat');

    if (period.dataPeriod === 'custom') {
      let start = '';
      let end = '';
      let customTzOffset = offset;

      if (period.timeRange.interval === IntervalType.Month) {
        start = dayjs.tz(period.timeRange.gte, '+00:00').format(formatStr);
        end = dayjs
          .tz(period.timeRange.lte, '+00:00')
          .add(1, 'month')
          .add(-1, 'second')
          .format(formatStr);
        customTzOffset = '+00:00';
      } else if (period.timeRange.interval === IntervalType.Day) {
        start = dayjs.tz(period.timeRange.gte, '+00:00').format(formatStr);
        end = dayjs.tz(period.timeRange.lte, '+00:00').format(formatStr);
      } else {
        //by hour
        const longFormat = t('common.momentDateFormat');
        start = dayjs.tz(period.timeRange.gte, '+00:00').format(longFormat);
        end = dayjs
          .tz(period.timeRange.lte, '+00:00')
          .add(-1, 'second')
          .format(longFormat);
      }

      return { start, end, timezoneOffset: customTzOffset };
    }
    return getTimePeriod(period.dataPeriod, offset, formatStr);
  }, [period, t, offset]);

  useEffect(() => {
    const cb = () =>
      setPeriod({
        dataPeriod: DataPeriod.LastDay,
        timezoneOffset: offset,
      });

    cb();
    register(cb);
    return () => unregister(cb);
  }, []);

  return (
    <>
      <Flex align="center" gap="8px" alignSelf="start">
        <TextLabel className={style.itemLabel}>
          {t('reportCommon.labels.dataPeriod')}:
        </TextLabel>
        <Tip />
      </Flex>
      <Stack>
        <ReportDataPeriodSelector period={period} setPeriod={setPeriod} />
        {dataPeriodRange && (
          <Flex pt="2x" align="center" gap="8px">
            <Text color={colorStyle.color.secondary}>
              {dataPeriodRange?.start + ' - ' + dataPeriodRange?.end}
              &nbsp;&nbsp;&nbsp;&nbsp;
              {'UTC' + dataPeriodRange.timezoneOffset}
              &nbsp;
            </Text>
            <Info content={t('reportCommon.tips.hints.dataPeriodTimezone')} />
          </Flex>
        )}
      </Stack>
    </>
  );
};

export default ReportDataPeriod;
